import React from 'react';
import { useId } from '@reach/auto-id';

import { useField, useFieldDescription } from '../../context/form.context';
import { ErrorMessage } from '../error-message.component';
import { RadioGroup } from '../radio-group';
import { FieldLabel } from './field-label.component';

export type RadioGroupFieldProps = {
  name: string;
  label?: string;
  showRequiredIndicator?: boolean;
  className?: string;
} & Omit<
  React.ComponentProps<typeof RadioGroup>,
  'value' | 'onChange' | 'hasError'
>;

export const RadioGroupField = ({
  name,
  label: labelProp,
  showRequiredIndicator = true,
  className,
  ...rest
}: RadioGroupFieldProps) => {
  const [field, meta] = useField(name);
  const { fieldLabel, required } = useFieldDescription(name);
  const label = labelProp ?? fieldLabel;
  const { error, touched } = meta;
  const hasError = Boolean(error && touched);
  const labelId = useId();
  const errorId = useId()!;

  return (
    <div
      className={className}
      role="radiogroup"
      aria-labelledby={labelId}
      aria-describedby={hasError ? errorId : undefined}
      aria-required={required}
    >
      <FieldLabel
        id={labelId}
        hasError={hasError}
        showRequiredIndicator={required && showRequiredIndicator}
      >
        {label}
      </FieldLabel>
      <RadioGroup hasError={hasError} className="ml-1" {...field} {...rest} />
      {hasError && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
    </div>
  );
};
